import React from 'react';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/solid';
import { isAssetMarketFieldsApplicable } from '../../viewServices/snapshot_form_shared';

/** Editable snapshot line items (modals: new/edit snapshot). */
const SnapshotEditableItemsTable = ({
  rows,
  updateRow,
  addRow,
  removeRow,
  assets = [],
  submitting,
}) => (
  <div>
    <div className="mb-2 flex items-center justify-between">
      <span className="text-sm font-semibold text-gray-800">
        Items
        <span className="ml-1 font-normal text-xs text-gray-400 tabular-nums">({rows.length})</span>
      </span>
      <button
        type="button"
        onClick={addRow}
        disabled={submitting}
        className="inline-flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-800 hover:text-green-900"
      >
        <PlusIcon className="h-4 w-4" />
        Row
      </button>
    </div>

    <div className="overflow-hidden overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-[720px] w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
          <tr>
            <th className="w-10 px-2 py-2 text-left" aria-label="Remove" />
            <th className="min-w-[160px] px-2 py-2 text-left">Asset</th>
            <th className="min-w-[100px] px-2 py-2 text-left">Source</th>
            <th className="min-w-[90px] px-2 py-2 text-left">Value (R$)</th>
            <th className="min-w-[80px] px-2 py-2 text-left">Qty</th>
            <th className="min-w-[100px] px-2 py-2 text-left">Mkt unit.</th>
            <th className="min-w-[200px] px-2 py-2 text-left">market_source (JSON)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const selectedAsset = r.asset?.trim()
              ? (assets || []).find((a) => String(a.id) === r.asset.trim())
              : null;
            const marketApplicable = isAssetMarketFieldsApplicable(selectedAsset);
            return (
            <tr key={r.key} className="border-t border-gray-100 align-top">
              <td className="px-1 py-1.5">
                <button
                  type="button"
                  onClick={() => removeRow(r.key)}
                  disabled={submitting || rows.length <= 1}
                  className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                  title="Remove row"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </td>
              <td className="px-2 py-1.5">
                <select
                  value={r.asset}
                  onChange={(e) => updateRow(r.key, 'asset', e.target.value)}
                  disabled={submitting}
                  className="w-full rounded border bg-white px-1 py-1 text-xs"
                >
                  <option value="">—</option>
                  {(assets || []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.asset_code ?? a.id} — {a.asset_name ?? '—'}
                    </option>
                  ))}
                </select>
              </td>
              <td className="px-2 py-1.5">
                <input
                  type="text"
                  value={r.snapshot_source}
                  onChange={(e) => updateRow(r.key, 'snapshot_source', e.target.value)}
                  disabled={submitting}
                  className="w-full rounded border px-1 py-1 text-xs"
                  maxLength={50}
                />
              </td>
              <td className="px-2 py-1.5">
                <input
                  type="text"
                  value={r.snapshot_value}
                  onChange={(e) => updateRow(r.key, 'snapshot_value', e.target.value)}
                  disabled={submitting}
                  className="w-full rounded border px-1 py-1 text-xs tabular-nums"
                  inputMode="decimal"
                />
              </td>
              <td className="px-2 py-1.5">
                <input
                  type="text"
                  value={r.snapshot_quantity}
                  onChange={(e) => updateRow(r.key, 'snapshot_quantity', e.target.value)}
                  disabled={submitting}
                  className="w-full rounded border px-1 py-1 text-xs tabular-nums"
                  inputMode="decimal"
                />
              </td>
              <td className="px-2 py-1.5">
                {marketApplicable ? (
                  <input
                    type="text"
                    value={r.market_unitary_value}
                    onChange={(e) => updateRow(r.key, 'market_unitary_value', e.target.value)}
                    disabled={submitting}
                    className="w-full rounded border px-1 py-1 text-xs tabular-nums"
                    inputMode="decimal"
                  />
                ) : (
                  <span className="block py-1 text-xs text-gray-400">Not applicable</span>
                )}
              </td>
              <td className="px-2 py-1.5">
                {marketApplicable ? (
                  <textarea
                    value={r.market_source_json}
                    onChange={(e) => updateRow(r.key, 'market_source_json', e.target.value)}
                    disabled={submitting}
                    rows={2}
                    className="min-h-[2.5rem] w-full resize-y rounded border px-1 py-1 font-mono text-xs"
                    placeholder="{}"
                  />
                ) : (
                  <span className="block py-1 text-xs text-gray-400">Not applicable</span>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

export default SnapshotEditableItemsTable;
